import React, { useState } from 'react';
import { Link } from 'react-router-dom';

function PricingPage() {
  const [selectedPlan, setSelectedPlan] = useState('lifetime');

  const freeFeatures = [
    'Upload CSV & XLSX files',
    'Full text visibility',
    'Up to 3 saved views',
    'Export to CSV',
    'Presentation mode',
    'Local data processing',
  ];

  const premiumFeatures = [
    'Unlimited saved views',
    'AI smart search',
    'Cloud file storage',
    'Share links with anyone',
    'Team collaboration',
    'Priority support',
  ];

  const pricingOptions = [
    { key: 'monthly', label: 'Monthly', price: '$12.99', period: '/month', note: null, badge: null },
    { key: 'annual', label: 'Annual', price: '$49', period: '/year', note: '$4.08/month', badge: null },
    { key: 'lifetime', label: 'Lifetime', price: '$69', period: ' one-time', note: 'Pay once, use forever', badge: 'BEST VALUE' },
  ];

  return (
    <div className="min-h-screen bg-cream-100">
      {/* Nav */}
      <nav className="border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/assets/logo_2.png" alt="QuickSheet Logo" className="h-8 w-8" />
            <span className="font-bold text-xl text-navy-900">QuickSheet</span>
          </Link>
          <Link
            to="/"
            className="text-gray-600 hover:text-navy-900 transition text-sm"
          >
            Back to home
          </Link>
        </div>
      </nav>

      {/* Header */}
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500 mb-4">Pricing</p>
        <h1 className="text-4xl md:text-5xl font-serif text-navy-900 mb-4">
          Simple, <span className="text-copper-500 italic">transparent</span> pricing
        </h1>
        <p className="text-lg text-gray-600 mb-12">
          Start free, upgrade when you need more
        </p>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto items-start">
          {/* Free Tier */}
          <div className="bg-cream-50 border-2 border-cream-300 rounded-2xl p-8 text-left">
            <h3 className="text-lg font-serif font-semibold text-navy-900 mb-2">Free</h3>
            <div className="mb-6">
              <span className="text-4xl font-bold text-navy-900">$0</span>
              <span className="text-gray-500 ml-1">/month</span>
            </div>
            <p className="text-gray-600 text-sm mb-6">
              Everything you need to view and analyze spreadsheets
            </p>
            <Link
              to="/auth"
              className="block w-full py-3 text-center border-2 border-cream-300 text-navy-900 font-semibold rounded-lg hover:bg-cream-200 transition-all mb-8"
            >
              Get Started
            </Link>
            <ul className="space-y-3">
              {freeFeatures.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-3 text-sm text-gray-700">
                  <svg className="w-5 h-5 text-copper-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Premium Tier */}
          <div className="bg-cream-50 border-2 border-copper-500 rounded-2xl p-8 text-left relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-copper-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              COMING SOON
            </div>
            <h3 className="text-lg font-serif font-semibold text-navy-900 mb-4">Premium</h3>

            {/* Pricing Options */}
            <div className="space-y-2 mb-6">
              {pricingOptions.map((option) => (
                <button
                  key={option.key}
                  onClick={() => setSelectedPlan(option.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all text-left ${
                    selectedPlan === option.key
                      ? 'border-copper-500 bg-copper-500/5'
                      : 'border-cream-300 hover:border-cream-200 hover:bg-cream-200'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedPlan === option.key ? 'border-copper-500' : 'border-cream-300'
                  }`}>
                    {selectedPlan === option.key && (
                      <div className="w-2 h-2 rounded-full bg-copper-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-navy-900">{option.label}</span>
                      {option.badge && (
                        <span className="text-[10px] bg-copper-500 text-white px-1.5 py-0.5 rounded-full font-bold">
                          {option.badge}
                        </span>
                      )}
                    </div>
                    {option.note && (
                      <span className="text-xs text-gray-500">{option.note}</span>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-lg font-bold text-navy-900">{option.price}</span>
                    <span className="text-xs text-gray-500">{option.period}</span>
                  </div>
                </button>
              ))}
            </div>

            <button
              disabled
              className="block w-full py-3 text-center bg-copper-500 text-white font-semibold rounded-lg opacity-50 cursor-not-allowed mb-8"
            >
              Coming Soon
            </button>
            <p className="text-xs text-gray-500 mb-4">Everything in Free, plus:</p>
            <ul className="space-y-3">
              {premiumFeatures.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-3 text-sm text-gray-700">
                  <svg className="w-5 h-5 text-copper-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-cream-300 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            &copy; 2026 QuickSheet. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default PricingPage;
